import {ResolvingLitElement} from "../../lib/ResolvingLitElement.js";
import {html} from "lit";
import {CustomerRepository} from "../../crm/CustomerRepository.js";
import {DateTime} from "luxon";
import {TimesheetEntryRepository} from "../TimesheetEntryRepository.js";
import {TimeTool} from "../../lib/TimeTool.js";
import {mdiAlert} from "@mdi/js";

export class BillingPeriodWidget extends ResolvingLitElement {

    static properties = {

    }

    /**
     * @private {ReactiveArray<TimesheetEntry>}
     */
    _entries;

    /**
     * @private {TimesheetEntryRepository}
     */
    _entryRepo;

    /**
     * @private {CustomerRepository}
     */
    _customerRepo;

    _maxQueryMonth = 3;

    constructor() {
        super();
        /** @type {Map<string,{customer:Customer,queryDate:Date,money:number,moneyFormatted:string,totalSeconds:number,billableSeconds:number,notBillableSeconds:number,dateWarning:boolean}>} **/
        this._byCustomerStat = new Map();
    }

    render() {
        return html`<div class="card weekly-time-summary">
                <header>
                    <h4>Not invoiced by customer</h4>
                </header>
                <table class="striped next-bills">
                    <thead>
                        <tr>
                            <th></th>
                            <th>Last bill</th>
                            <th>Hours</th>
                            <th>Amount</th>
                        </tr>
                    </thead>
                    <tbody>
                    ${Array.from(this._byCustomerStat.values()).map( (item, key) => {
                        return html`<tr>
                            <th><a href="javascript:;" @click="${this._customerClicked}" data-customer-id="${item.customer.id}">${item.customer.name}</a>${item.dateWarning ? html`<svg style="width:24px;height:24px;display:inline-block; margin-left: 5px;" viewBox="0 0 24 24"><path fill="#d59c07" d="${mdiAlert}" /></svg>` : ''}</th>
                            <td class="text-right">
                                ${item.customer.lastInvoiceAt ? item.customer.lastInvoiceAt.toLocaleDateString() : html`<span class="text-grey">None</span>`}</td>
                            <td>${this._formatSeconds(item.billableSeconds)}${item.notBillableSeconds ? html`<br><span class="non-billable">${this._formatSeconds(item.notBillableSeconds)}</span>` : ''}</td>
                            <td>${item.moneyFormatted}</td>
                        </tr>`
                    })}
                    </tbody>
                    <tfoot>
                        <tr>
                            <th></th>
                            <td></td>
                            <td>${this._totalBillableHours()}${this._totalNotBillableHours() !== '00:00' ? html`<br><span class="non-billable">${this._totalNotBillableHours()}</span>` : ''}</td>
                            <td><strong>${this._totalMoney()}</strong></td>
                        </tr>
                    </tfoot>
                </table>
            </div>`;
    }

    _customerClicked(evt) {
        this.dispatchEvent(new CustomEvent('customer-clicked', {detail: evt.target.dataset.customerId, bubbles:true,composed:true}));
    }

    connectedCallback() {
        super.connectedCallback();
        this._entries = this._get('timesheet-entries');
        this._entryRepo = this._resolve(TimesheetEntryRepository);
        this._customerRepo = this._resolve(CustomerRepository);

    }

    firstUpdated(_changedProperties) {
        super.firstUpdated(_changedProperties);
        this._customerRepo.all().then(customers => {this._loadData(customers)});
        //this._entries.filled.listen(this._updateWidgetData);
    }

    _totalMoney() {
        let money = 0;
        for (let stat of this._byCustomerStat) {
            money += stat[1].money
        }
        const numberFormatter = new Intl.NumberFormat('de-DE');
        return numberFormatter.format(money.toFixed(2)) + '€';
    }

    _totalBillableHours() {
        let hours = 0;
        for (let stat of this._byCustomerStat) {
            hours += stat[1].billableSeconds
        }
        return TimeTool.formatDurationFromSeconds(hours, false);
    }

    _totalNotBillableHours() {
        let hours = 0;
        for (let stat of this._byCustomerStat) {
            hours += stat[1].notBillableSeconds;
        }
        return TimeTool.formatDurationFromSeconds(hours, false);
    }

    /**
     * @param {Customer[]} customers
     * @private
     */
    _loadData(customers) {
        let minDate = DateTime.now().minus({months:this._maxQueryMonth}).toJSDate();

        let stats = new Map();
        let minQueryDate = new Date();

        for(let customer of customers) {
            let dateWarning = true;
            let queryDate = new Date(minDate.getTime());
            if (customer.lastInvoiceAt && customer.lastInvoiceAt.getTime() > queryDate) {
                queryDate = DateTime.fromJSDate(customer.lastInvoiceAt).plus({days:1}).toJSDate();
                queryDate.setHours(0,0,0);

                dateWarning = false;
            }

            if (queryDate.getTime() < minQueryDate.getTime()) {
                minQueryDate = queryDate;
            }


            stats.set(customer.id, {
                customer: customer,
                queryDate: queryDate,
                money: 0,
                moneyFormatted: "",
                totalSeconds: 0,
                billableSeconds: 0,
                notBillableSeconds: 0,
                dateWarning: dateWarning
            });
        }

        this._entryRepo.getDuring(minQueryDate, new Date()).then(entries => {
            this._entries.fill(...entries);
            this._byCustomerStat = this._createStat(stats, entries);
            this.requestUpdate();
        });

    }

    /**
     * @param {Map<string,{customer:Customer,queryDate:Date,money:number,moneyFormatted:string,totalSeconds:number,billableSeconds:number,notBillableSeconds:number,dateWarning:boolean}>} stats
     * @param {TimesheetEntry[]} entries
     * @private
     */
    _createStat(stats, entries) {

        for (let entry of entries) {
            if (!entry.belongsToCustomer()) {
                continue;
            }

            let customerStat = stats.get(entry.task.customer.id);

            if (customerStat.queryDate && customerStat.queryDate.getTime() > entry.end.getTime()) {
                continue;
            }

            customerStat.totalSeconds += entry.duration;
            if (entry.billable) {
                customerStat.billableSeconds += entry.duration;
            } else {
                customerStat.notBillableSeconds += entry.duration;
            }

        }

        const numberFormatter = new Intl.NumberFormat('de-DE');

        stats.forEach(stat => {
            let rate = stat.customer.hourlyRate ? stat.customer.hourlyRate : 0;
            stat.money = stat.billableSeconds/3600*rate;
            stat.moneyFormatted = stat.money ? numberFormatter.format(stat.money.toFixed(2)) + '€' : html`<span class="text-grey">0,00€</span>`;
        });

        return stats;

    }

    /**
     * @param {number} seconds
     * @return {TemplateResult<1>|string}
     * @private
     */
    _formatSeconds(seconds) {
        if (!seconds || seconds < 60) {
            return html`<span class="text-grey">00:00</span>`;
        }
        return TimeTool.formatDurationFromSeconds(seconds, false);
    }

    createRenderRoot() {
        return this;
    }
}

customElements.define('x-billing-period-widget', BillingPeriodWidget);